import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { CreateGastoDto, UpdateGastoDto, ReadGastoDto, ReadTipoGastoDto, ReadPersonSelectDto } from '@/api.types';
import { DeleteGasto, GetGasto, SetGasto } from './gasto.service';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { GetTipoGastos } from '../tipo-gasto/tipo-gasto.service';
import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Tabelas } from '@/shared/constants/tables';
import { convertToGMTPlus3 } from '@/shared/utils/datas';
import { readForSelect } from '@/modules/people/person.service';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export default function GastoEditPage() {
  const { setActions } = useBreadcrumbActions();
  const navigate = useNavigate();
  const params = useParams();

  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', href: Links.Gasto_Criar, onClick: () => {} },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions]);

  const [tipoGastos, setTipoGastos] = useState<ReadTipoGastoDto[]>([]);
  const [pessoas, setPessoas] = useState<ReadPersonSelectDto[]>([]);

  const { register, handleSubmit, reset, watch } = useForm<ReadGastoDto>();

  const editadoPor = watch('editadoPor');
  const editadoEm = watch('editadoEm');
  const tipoGastoId = watch('tipoGasto.id');
  const tipoGastoSelecionado = tipoGastos.find(tg => tg.id === tipoGastoId);

  useEffect(() => {
    Promise.all([
      GetTipoGastos(0, 1000)
        .then((result) => {
          setTipoGastos(result.objects);
        })
        .catch((res) => {
          toast.error((res as string) + '');
        }),
      readForSelect({ skip: 0, take: 1000, isUsuario: true, comUsuario: true, papelId: 1 })
        .then((result) => {
          setPessoas(result[0]);
        })
        .catch((res) => {
          toast.error((res as string) + '');
        }),
      id > 0
        ? GetGasto(id)
          .then((result) => {
            if (result) {
              const dataFormatted = {
                ...result,
                data: result.data ? new Date(result.data).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              };
              reset(dataFormatted);
            }
          })
          .catch((res) => {
            toast.error((res as string) + '');
          })
        : Promise.resolve(),
    ]);
  }, [id, reset]);

  const onSubmit: SubmitHandler<ReadGastoDto> = (data) => {
    setIsLoading(true);

    const dataToSave: CreateGastoDto | UpdateGastoDto = {
      id: id > 0 ? id : undefined,
      data: convertToGMTPlus3(data.data).toISOString(),
      quemGastou: { id: data.quemGastou?.id || '' },
      valor: Number(data.valor) || 0,
      tipoGasto: { id: Number(data.tipoGasto?.id) },
      valorColuna1: data.valorColuna1 || undefined,
      valorColuna2: data.valorColuna2 || undefined,
      valorColuna3: data.valorColuna3 || undefined,
    };

    SetGasto(dataToSave)
      .then((res) => {
        toast.success(res + '');
        navigate(`${Links.Gasto_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      })
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setModalExcluirShow(false);
    DeleteGasto(id)
      .then((res) => {
        toast.success(res + '');
        navigate(`${Links.Gasto_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4">Gastos do Setor</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-2">
            <label htmlFor="data" className="form-label">
              Data:
            </label>
            <input
              id="data"
              title="Data"
              type="date"
              className="form-control"
              {...register('data', { required: true })}
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="quemGastou" className="form-label">
              Quem Gastou:
            </label>
            <select
              id="quemGastou"
              title="Quem Gastou"
              className="form-select"
              {...register('quemGastou.id', { required: true })}
            >
              <option value="">Selecione...</option>
              {pessoas.map((pessoa) => (
                <option key={pessoa.id} value={pessoa.id}>
                  {pessoa.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label htmlFor="valor" className="form-label">
              Valor:
            </label>
            <div className="input-group">
              <span className="input-group-text">R$</span>
              <input
                id="valor"
                title="Valor"
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                {...register('valor', { required: true, valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="col-md-3 mb-3">
            <label htmlFor="tipoGasto" className="form-label">
              Tipo de Gasto:
            </label>
            <select
              id="tipoGasto"
              title="Tipo de Gasto"
              className="form-select"
              {...register('tipoGasto.id', { required: true, valueAsNumber: true })}
            >
              <option value="">Selecione...</option>
              {tipoGastos.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.descricao}
                </option>
              ))}
            </select>
          </div>

          {tipoGastoSelecionado && (
            <>
              {tipoGastoSelecionado.coluna1 && (
                <div className="mb-3 col-md-4">
                  <label htmlFor="valorColuna1" className="form-label">
                    {tipoGastoSelecionado.coluna1}:
                  </label>
                  <input
                    id="valorColuna1"
                    title={tipoGastoSelecionado.coluna1}
                    type="text"
                    className="form-control"
                    {...register('valorColuna1')}
                  />
                </div>
              )}

              {tipoGastoSelecionado.coluna2 && (
                <div className="mb-3 col-md-4">
                  <label htmlFor="valorColuna2" className="form-label">
                    {tipoGastoSelecionado.coluna2}:
                  </label>
                  <input
                    id="valorColuna2"
                    title={tipoGastoSelecionado.coluna2}
                    type="text"
                    className="form-control"
                    {...register('valorColuna2')}
                  />
                </div>
              )}

              {tipoGastoSelecionado.coluna3 && (
                <div className="mb-3 col-md-4">
                  <label htmlFor="valorColuna3" className="form-label">
                    {tipoGastoSelecionado.coluna3}:
                  </label>
                  <input
                    id="valorColuna3"
                    title={tipoGastoSelecionado.coluna3}
                    type="text"
                    className="form-control"
                    {...register('valorColuna3')}
                  />
                </div>
              )}
            </>
          )}

          <EditadoPorRodape
            dados={{
              editadoPor: editadoPor ? { username: editadoPor.username } : undefined,
              editadoEm: editadoEm,
            }}
          />
          <div>
            <BotoesSalvarExcluir
              isLoading={isLoading}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <ModalExcluir
        show={modaExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      />
      <ModalHistorico
        show={modalHistorico}
        handleHide={() => setModalHistorico(false)}
        tabela={Tabelas.gasto}
        idRegistro={id}
      />
    </>
  );
}